import _ from 'lodash';
import { selectAnnotatedItemIds } from '@/annotation/annotation.store';
import {
  sqAnnotationStore,
  sqDurationStore,
  sqTrendConditionStore,
  sqTrendSeriesStore,
  sqTrendStore,
  sqWorkbenchStore,
} from '@/core/core.stores';
import { getScaledFontSize } from '@/core/html.utilities';
import { nanosToMillis } from '@/datetime/dateTime.utilities';
import { getDefaultCapsuleDataLabelSettings } from '@/trend/trendDataHelper.utilities';
import { decorate } from '@/trend/trendViewer/itemDecorator.utilities';
import { getCapsuleColumnTitle } from '@/trend/trendViewer/trendViewer.utilities';
import { getDurationDisplayRange } from '@/trendData/duration.utilities';
import { getUniqueId, isCapsuleAggregated } from '@/trendData/trend.utilities';
import { computeChartZones, splitUniqueId } from '@/trendData/trendCapsule.utilities';
import {
  BREAK_SIZE,
  CAPSULE_PANEL_LOOKUP_COLUMNS,
  CAPSULE_PANEL_TREND_COLUMNS,
  DEFAULT_CAPSULE_LINE_WIDTH,
  ITEM_TYPES,
  PREVIEW_ID,
  SAMPLE_OPTIONS,
  TREND_PANELS,
  TREND_VIEWS,
  TrendColorMode,
  ZERO_LENGTH_IDENTIFIER,
} from '@/trendData/trendData.constants';
import { CAPSULE_LABEL_HEIGHT_REM } from '@/utilities/chartLanes.constants';
import { getColumnValueAndUOM } from '@/utilities/columnHelper.utilities';
import { isStatColumn } from '@/utilities/formula.utilities';
import { getCertainId } from '@/utilities/id.utilities';
import { isCapsuleFullyVisible } from '@/utilities/utilities';
import { SeeqNames } from '@seeqdev/constants';
import type { SelectedCapsule } from '@/trendData/trendCapsule.store';
import type {
  CalculatedChartCapsule,
  CapsuleCalculatedChartItem,
  CapsuleChartItem,
  CapsuleItem,
  PreviewCapsulesDefinition,
} from '@/trendData/trendData.types';
import type { AnyProperty } from '@/utilities.types';

/**
 * Derives the chart capsules (`chartItems`) from the capsule dataset. The derivation is shared by the
 * flux store handler and, once the capsule runtime store exists, its recompute path, so the two cannot
 * drift apart. `deriveChartItems` is a pure function of its inputs; `getChartCapsulesContext` gathers
 * everything the derivation needs from the other stores, so the derivation itself never reads a store.
 */

// Highcharts draws series colors from options rather than CSS (we do not run styledMode), so the
// uncertain-portion fill must be a literal that matches the chart background in each theme.
const DARK_COLOR_FOR_UNCERTAIN_PORTION = '#262c36';
const LIGHT_COLOR_FOT_UNCERTAIN_PORTION_WHITE = '#fff';

export type StitchBreak = {
  from: number;
  to: number;
  breakSize: number;
};

export type StitchTime = {
  start: number;
  end: number;
  duration: number;
};

export type CapsuleTiming = {
  start: number;
  end: number;
  isChildOf: string;
};

export interface StitchDetailsResult {
  stitchTimes: StitchTime[];
  capsulesTimings: CapsuleTiming[];
  /** Only meaningful when `tooManyBreaks` is false; the caller keeps its previous breaks otherwise */
  stitchBreaks: StitchBreak[];
  /** True when there are more breaks than pixels, which chain view cannot render */
  tooManyBreaks: boolean;
}

/**
 * Derives the chain-view stitch details from the given capsules: the visible time periods, the
 * per-capsule timings, and the Highcharts break definitions for the gaps between them.
 */
export function deriveStitchDetails(
  capsules: CapsuleItem[],
  context: { numPixels: number; displayRangeStart: number; displayRangeEnd: number },
): StitchDetailsResult {
  const times: number[] = [];
  const capsulesTimings = _.chain(capsules)
    .reject((capsule) => capsule.startTime === capsule.endTime && capsule.startTime !== null)
    .map((capsule) => {
      // bound any partially visible capsules to the display range
      let startTime = capsule.startTime;
      if (!startTime || startTime < context.displayRangeStart) {
        startTime = context.displayRangeStart;
      }
      let endTime = capsule.endTime;
      if (!endTime || endTime > context.displayRangeEnd) {
        endTime = context.displayRangeEnd;
      }

      return {
        start: startTime,
        end: endTime,
        isChildOf: capsule.isChildOf,
      };
    })
    .sortBy(['start'])
    .forEach((capsule) => {
      // If the start time is greater than the last entry, add the start time and the end time to the array
      if (times.length === 0 || capsule.start > times[times.length - 1]) {
        times.push(capsule.start);
        times.push(capsule.end);
      } else if (capsule.end > (_.last(times) ?? -Infinity)) {
        times[times.length - 1] = capsule.end;
      }
    })
    .value();

  const stitchTimes = _.transform(_.chunk(times, 2), (result: StitchTime[], [start, end]) => {
    result.push({ start, end, duration: end - start });
  });

  if (stitchTimes.length === 0) {
    return { stitchTimes: [], capsulesTimings: [], stitchBreaks: [], tooManyBreaks: false };
  }

  // If the times array doesn't start or end with the display range, make sure that there is a break
  if (_.head(times) !== context.displayRangeStart) {
    times.unshift(context.displayRangeStart);
  } else {
    times.shift();
  }

  if (_.last(times) !== context.displayRangeEnd) {
    times.push(context.displayRangeEnd);
  } else {
    times.pop();
  }

  const breaks = _.transform(_.chunk(times, 2), function (result: StitchBreak[], chunk) {
    result.push(_.zipObject(['from', 'to'], chunk) as StitchBreak);
  });

  if (breaks.length >= context.numPixels) {
    return { stitchTimes, capsulesTimings, stitchBreaks: [], tooManyBreaks: true };
  }

  _.forEach(breaks, (thisBreak) => {
    thisBreak.breakSize = parseFloat(BREAK_SIZE);
  });

  return { stitchTimes, capsulesTimings, stitchBreaks: breaks, tooManyBreaks: false };
}

/** A capsule-panel column with the reference-series format options already resolved */
type ChartCapsuleColumn = AnyProperty & { key: string; referenceSeries?: string; referenceFormatOptions?: unknown };

export interface ChartCapsulesContext {
  view: string;
  colorMode: TrendColorMode;
  displayRangeStart: number;
  displayRangeEnd: number;
  displayRangeDurationMs: number;
  darkMode: boolean;
  /** Item ids that carry annotations, so annotated conditions draw thicker */
  annotatedItemIds: Record<string, unknown>;
  /** Enabled chart-capsule columns with `referenceFormatOptions` resolved from the series store */
  capsuleColumns: ChartCapsuleColumn[];
  /** Details-pane order of each condition, for chart-item ordering */
  conditionIndexById: Record<string, number>;
  /** First asset name per condition, for the asset column */
  conditionAssetNameById: Record<string, string | undefined>;
}

export interface ChartCapsulesInputs {
  calculatedChartItems: CapsuleCalculatedChartItem[];
  previewChartItem: CapsuleCalculatedChartItem;
  previewCapsulesDefinition: PreviewCapsulesDefinition | undefined;
  editingId: string | undefined;
  items: CapsuleItem[];
  stitchBreaks: AnyProperty[];
  selectedCapsules: SelectedCapsule[];
}

/**
 * Gathers the cross-store context the chart-capsule derivation depends on. Kept beside the derivation
 * so the flux handler and the dispatching actions assemble identical context.
 */
export function getChartCapsulesContext(): ChartCapsulesContext {
  const columns = _.concat(CAPSULE_PANEL_TREND_COLUMNS, _.find(CAPSULE_PANEL_LOOKUP_COLUMNS, { key: 'asset' }));
  const capsuleColumns = _.chain(columns)
    // CAPSULE panel version must be used because they have the property or statistic path
    .concat(sqTrendStore.propertyColumns(TREND_PANELS.CAPSULES))
    .concat(sqTrendStore.customColumns(TREND_PANELS.CAPSULES))
    .filter((column) => sqTrendStore.isColumnEnabled(TREND_PANELS.CHART_CAPSULES, column.key))
    .map((column) => ({
      ...column,
      referenceFormatOptions: column.referenceSeries
        ? (sqTrendSeriesStore.findItem(column.referenceSeries)?.formatOptions ?? null)
        : null,
    }))
    .value();
  const displayRange = sqDurationStore.getState().displayRange;
  return {
    view: sqTrendStore.view,
    colorMode: sqTrendStore.trendColorSettings.colorMode,
    displayRangeStart: displayRange.start,
    displayRangeEnd: displayRange.end,
    displayRangeDurationMs: getDurationDisplayRange().duration.asMilliseconds(),
    darkMode: sqWorkbenchStore.darkMode,
    annotatedItemIds: selectAnnotatedItemIds(sqAnnotationStore.getState()),
    capsuleColumns,
    conditionIndexById: _.chain(sqTrendConditionStore.items)
      .map((item, index) => [item.id, index])
      .fromPairs()
      .value(),
    conditionAssetNameById: _.chain(sqTrendConditionStore.items)
      .map((item) => [item.id, item.assets?.[0]?.name])
      .fromPairs()
      .value(),
  };
}

/**
 * Derives the chart capsules from the capsule dataset and the given context.
 */
export function deriveChartItems(inputs: ChartCapsulesInputs, context: ChartCapsulesContext): CapsuleChartItem[] {
  let allCapsules: CapsuleCalculatedChartItem[];
  let index = -1;
  let calculatedItems = inputs.calculatedChartItems;
  const editingId = inputs.editingId;
  const previewCapsuleDef = inputs.previewCapsulesDefinition;
  const displayPreview = !_.isEmpty(previewCapsuleDef);

  if (!_.isNil(editingId) && editingId !== PREVIEW_ID && displayPreview) {
    calculatedItems = _.cloneDeep(inputs.calculatedChartItems);
    index = _.findIndex(calculatedItems, { id: editingId });
    calculatedItems.splice(index, 1, inputs.previewChartItem);
    allCapsules = calculatedItems;
  } else if (displayPreview) {
    allCapsules = calculatedItems.concat(inputs.previewChartItem);
  } else {
    allCapsules = calculatedItems;
  }

  const shouldUseTableCapsules =
    context.view === TREND_VIEWS.CHAIN ||
    (context.view === TREND_VIEWS.CALENDAR && context.colorMode !== TrendColorMode.Item);
  const anySelectedTableCapsuleInDisplay = _.some(inputs.items, (item) => item.selected);
  const shouldUseSelectedTableCapsules = shouldUseTableCapsules && anySelectedTableCapsuleInDisplay;
  const shouldDisplayAllCapsules = context.view === TREND_VIEWS.CALENDAR;
  const stitchBreaks = inputs.stitchBreaks;
  const items = inputs.items;
  const chartCapsules = _.chain(allCapsules)
    .sortBy((condition) => context.conditionIndexById[condition.id] ?? -1)
    .sortBy((condition) => (condition.id === PREVIEW_ID ? 1 : 0))
    .flatMap((condition) =>
      prepareConditionForDisplay(
        condition,
        items,
        stitchBreaks,
        editingId as string,
        shouldUseTableCapsules,
        shouldUseSelectedTableCapsules,
        shouldDisplayAllCapsules,
        context,
      ),
    )
    .compact()
    .value();

  if (_.isEmpty(chartCapsules)) {
    return chartCapsules;
  }

  const fullyVisibleCapsules = _.chain(chartCapsules)
    .flatMap('capsules')
    .filter((capsule) => {
      return !capsule.notFullyVisible ? capsule : undefined;
    })
    .value();

  const selectedCapsules = inputs.selectedCapsules;
  const selectedIds = _.chain(selectedCapsules)
    .map((selectedCapsule) => {
      const { conditionId, capsuleId } = splitUniqueId(selectedCapsule.id);

      const isFullyVisibleCapsule =
        _.some(fullyVisibleCapsules, { id: selectedCapsule.id }) ||
        isCapsuleFullyVisible(
          selectedCapsule.startTime,
          selectedCapsule.endTime,
          context.displayRangeStart,
          context.displayRangeEnd,
        ); // for in-progress capsules
      if (!isFullyVisibleCapsule) {
        return;
      }

      const possibleCapsuleMatches = _.chain(calculatedItems).filter(['id', conditionId]).flatMap('capsules').value();

      const capsule = _.find(possibleCapsuleMatches, ['id', capsuleId]);
      if (capsule) {
        return getUniqueId(conditionId, capsule.id);
      }

      const maybeCapsule = _.find(
        possibleCapsuleMatches,
        (capsule: CalculatedChartCapsule) =>
          selectedCapsule.startTime === nanosToMillis(capsule.start as number) ||
          selectedCapsule.endTime === nanosToMillis(capsule.end as number),
      );

      return maybeCapsule ? getUniqueId(conditionId, maybeCapsule.id) : selectedCapsule.id;
    })
    .compact()
    .value();

  return _.map(chartCapsules, (row) => ({
    ...row,
    anyCapsulesSelected: _.some(selectedIds, (id) => splitUniqueId(id).conditionId === getCertainId(row.conditionId)),
    zones: computeChartZones(row, selectedIds) as CapsuleChartItem['zones'],
  }));
}

/**
 * Prepares a condition for display on the trend.
 * This function generates a series that is used to visualize the capsule results.
 * Capsules that would otherwise overlap are assigned to new 'rows' to ensure the start and end times of each
 * capsule are properly displayed.
 *
 * To ensure the preview displays properly if no capsules are found an empty row is added to ensure things look
 * right.
 *
 * This function also manages the proper display of uncertain capsules by adding an additional "uncertain" capsule
 * set that overlaps the original condition and helps create the start/end boundaries of the outline (Note: the
 * top and bottom outline are achieved by setting the lineHeight for uncertain capsules to a smaller width than the
 * the one of the capsules.)
 *
 * In chain-view, table capsules (`items`) are used to generate the series instead of condition capsules.
 * This is done because chain-view only displays those capsules that are in the capsule table and may show
 * incorrect selections if the condition capsules are used since they can be bucketized.
 *
 * @param condition - the condition to prepare for display.
 * @param tableCapsules - the capsules that are displayed in the table
 * @param stitchBreaks - the stitch breaks used in chain view
 * @param editingId - the id of the condition that is currently being edited.
 * @param shouldUseTableCapsules - true if table capsules should be used instead of condition capsules to create
 *   chart-items
 * @param shouldUseSelectedTableCapsules - true if chart-items should be limited to selected capsules in chain view
 * @param shouldDisplayAllCapsules - true if all capsules should display regardless of selection
 * @param context - the cross-store context gathered by `getChartCapsulesContext`
 *
 * @returns the condition prepared for display - as a series with 'zones' to support selection.
 */
function prepareConditionForDisplay(
  condition: CapsuleCalculatedChartItem,
  tableCapsules: CapsuleItem[],
  stitchBreaks: AnyProperty[],
  editingId: string,
  shouldUseTableCapsules: boolean,
  shouldUseSelectedTableCapsules: boolean,
  shouldDisplayAllCapsules: boolean,
  context: ChartCapsulesContext,
): CapsuleChartItem[] | undefined {
  if (_.isEmpty(condition)) {
    return;
  }

  const isEditing = _.startsWith(condition.id, PREVIEW_ID) || editingId === condition.id;
  const prevEnds: Record<number, number> = {};
  const displayRangeStart = context.displayRangeStart;
  const displayRangeEnd = context.displayRangeEnd;

  let totalYValue = 1;

  const capsuleColumns = context.capsuleColumns;

  const capsuleDataLabels: AnyProperty<{ label: string; titles: string[] }> = {};
  let lineWidth = Math.max(
    (condition.thickness ?? condition.lineWidth ?? 1) * DEFAULT_CAPSULE_LINE_WIDTH,
    context.annotatedItemIds[condition.id] ? 25 : 0,
  );

  const capsules = shouldUseTableCapsules
    ? _.chain(tableCapsules)
        .filter((capsule) => getCertainId(capsule.conditionId) === condition.id)
        .reject((capsule) => shouldUseSelectedTableCapsules && !capsule.selected && !shouldDisplayAllCapsules)
        .sortBy('endTime')
        .value()
    : condition.capsules;
  const isAggregated = _.chain(condition.capsules)
    .flatMap((capsule) => capsule.properties)
    .thru(isCapsuleAggregated)
    .value();
  const showLabels = (shouldUseSelectedTableCapsules || !isAggregated) && capsuleColumns.length > 0;

  // If data labels are enabled, compute the maximum line height so that all capsules use the same one
  if (showLabels) {
    _.chain(capsules)
      // Filter out those capsules that fall inside a break in chain view so that their labels don't leak out
      .reject<CalculatedChartCapsule | CapsuleItem>((capsule) => {
        if (shouldUseTableCapsules) {
          const capsuleStartTime = getCapsuleTimestampMilliseconds(capsule, 'start', shouldUseTableCapsules);

          return _.some(stitchBreaks, ({ from, to }) =>
            _.inRange(_.isNil(capsuleStartTime) ? displayRangeStart : capsuleStartTime, from, to),
          );
        }

        return false;
      })
      .forEach((capsule) => {
        const tableCapsule = shouldUseTableCapsules
          ? capsule
          : _.find(tableCapsules, ['id', getUniqueId(condition.id, capsule.id)]);
        const dataLabels = _.chain(capsuleColumns)
          // Can only get statistic values from table capsules, but that comes with the caveat that unbounded
          // or uncertain won't be found because their IDs won't match (which is also why those capsules can't be
          // selected in the capsules pane)
          .reject((column) => isStatColumn(column) && !tableCapsule)
          .map((column) => {
            const startTime = getCapsuleTimestampMilliseconds(capsule, 'start', shouldUseTableCapsules);
            const endTime = getCapsuleTimestampMilliseconds(capsule, 'end', shouldUseTableCapsules);
            const { referenceFormatOptions } = column;
            const capsuleWithData = isStatColumn(column)
              ? {
                  ...tableCapsule,
                  formatOptions: referenceFormatOptions,
                }
              : {
                  itemType: ITEM_TYPES.CAPSULE,
                  startTime,
                  endTime,
                  duration: startTime && endTime ? endTime - startTime : undefined,
                  properties: getCapsuleProperties(capsule, shouldUseTableCapsules),
                  propertiesUOM: getCapsulePropertiesUOM(capsule, shouldUseTableCapsules),
                };

            return {
              title: getCapsuleColumnTitle(column),
              value:
                column.key === 'asset'
                  ? context.conditionAssetNameById[condition.id]
                  : getColumnValueAndUOM(column, decorate({ items: capsuleWithData })),
            };
          })
          .reject(({ value }) => !_.isNumber(value) && _.isEmpty(value))
          .value();

        if (dataLabels.length) {
          lineWidth = Math.max(getScaledFontSize(CAPSULE_LABEL_HEIGHT_REM) * dataLabels.length, lineWidth);
          capsuleDataLabels[capsule.id] = {
            label: _.map(dataLabels, 'value').join('<br>'),
            titles: _.map(dataLabels, 'title'),
          };
        }
      })
      .value();
  }

  // De-conflict the capsules so as to figure out which row they should go on
  const chartItems = _.chain(capsules)
    .transform<CalculatedChartCapsule | CapsuleItem, CapsuleChartItem>((rows, capsule) => {
      let offset = 0;
      let nextPointStart = null;
      const startMilliseconds = getCapsuleTimestampMilliseconds(capsule, 'start', shouldUseTableCapsules);
      const endMilliseconds = getCapsuleTimestampMilliseconds(capsule, 'end', shouldUseTableCapsules);
      const cursorKeyMilliseconds = capsule.cursorKey ? nanosToMillis(capsule.cursorKey) : undefined;
      const notFullyVisible = !isCapsuleFullyVisible(
        startMilliseconds,
        endMilliseconds,
        context.displayRangeStart,
        context.displayRangeEnd,
      );
      const xStart = startMilliseconds ?? displayRangeStart;
      const xEnd = endMilliseconds ?? displayRangeEnd;
      const zeroLength = xStart === xEnd;

      // zeroLength capsules are placed on a separate series to ensure the marker property doesn't cause unwanted
      // artifacts (https://seeq.atlassian.net/browse/CRAB-37825); to properly determine the
      // available label width we need to store the actual nextPoint in a property that we can access during
      // the label rendering (calculating it on the fly is too expensive and causes the browser to freeze when a
      // large number of capsules is displayed).
      if (zeroLength) {
        let nextPointIndex = _.findIndex(condition.capsules, capsule);
        // since the data-precision for capsules is nanoseconds but the display precision is milliseconds it is
        // possible to have 2 capsules that show as identical. This while loop makes sure we get the visually
        // "next" point
        while (nextPointStart === null && nextPointIndex < _.size(condition.capsules)) {
          const nextUpStartInMillis = nanosToMillis(condition.capsules[nextPointIndex]?.start ?? 0);
          if (nextUpStartInMillis === startMilliseconds) {
            nextPointIndex++;
          } else {
            nextPointStart = nextUpStartInMillis;
          }
        }
      }

      // find the first non-overlapping row
      do {
        const prevEnd = _.get(prevEnds, offset, -Number.MAX_VALUE);

        if (xStart < prevEnd) {
          offset += lineWidth - 1;
        } else {
          prevEnds[offset] = xEnd;
          break;
        }
        // oxlint-disable-next-line no-constant-condition
      } while (true);

      const yValue = totalYValue + offset;

      // We need each colored set of the same capsule to be on the same row, but highcharts doesn't let us color
      // the connections between datums differently in a single series, so we have to split into multiple series
      // (ie, rows)
      const capsuleHasColor = 'color' in capsule && capsule.color !== undefined;
      const colorToUse: string = 'color' in capsule && capsule.color ? capsule.color : condition.color;
      // to prevent https://seeq.atlassian.net/browse/CRAB-37825 we need to make sure that zero-length rows are
      // separate from "regular" capsules; they need to however share the same y-Values to be properly de-conflicted
      let row = _.find(
        rows,
        (row) => row.data[0].y === yValue && row.isZeroLength === zeroLength && row.color === colorToUse,
      );

      if (!row) {
        const rowId = `${yValue}-${condition.id}${zeroLength ? ZERO_LENGTH_IDENTIFIER : ''}${
          capsuleHasColor ? `-${colorToUse}` : ''
        }`;
        row = {
          id: rowId,
          uniqueId: rowId,
          conditionId: condition.id,
          conditionName: condition.name,
          color: colorToUse,
          selected: condition.selected,
          lane: condition.lane,
          isPreviewItem: condition.isPreviewItem,
          itemType: ITEM_TYPES.CAPSULE,
          isAggregated,
          anyCapsulesSelected: false,
          data: [],
          capsules: [],
          zones: [],
          lineWidth: zeroLength ? DEFAULT_CAPSULE_LINE_WIDTH : lineWidth,
          dataLabels: getDefaultCapsuleDataLabelSettings(),
          yValue,
          sampleDisplayOption: zeroLength ? SAMPLE_OPTIONS.SAMPLES : SAMPLE_OPTIONS.LINE,
          isZeroLength: zeroLength,
          overlay: condition.overlay,
          orderInLane: condition.orderInLane,
        };
        rows.push(row);
      }

      const { label: dataLabelString, titles: dataLabelTitles } = capsuleDataLabels[capsule.id] ?? {};
      row.lineWidth = Math.max(lineWidth, row.lineWidth);
      row.dataLabels.enabled = row.dataLabels.enabled || !_.isEmpty(dataLabelString);
      row.dataLabels.labelNames = _.uniq(row.dataLabels.labelNames.concat(dataLabelTitles || []));

      // Create the line segment for this capsule
      row.data.push({
        // Move the x to the edge of the chart if it is off-screen so data labels show up
        x: Math.max(xStart, displayRangeStart),
        y: yValue,
        ...(nextPointStart && { nextPointStart }),
        ...(dataLabelString && {
          dataLabelString,
          labelText: dataLabelString,
        }),
      });

      row.data.push({ x: xEnd, y: yValue });

      // Create the space between the capsules.
      row.data.push({ x: xEnd, y: null });

      if (capsule.isUncertain && !capsule.cursorKey && row.firstFullyUncertainCapsuleStart === undefined) {
        row.firstFullyUncertainCapsuleStart = xStart;
      }

      // Keep a reference to the capsule for selection purposes
      row.capsules.push({
        id: shouldUseTableCapsules ? capsule.id : getUniqueId(condition.id, capsule.id),
        isReferenceCapsule: isTableCapsule(capsule, shouldUseTableCapsules)
          ? (capsule.isReferenceCapsule ?? undefined)
          : (_.get(
              _.find(capsule.properties, ['name', SeeqNames.CapsuleProperties.ReferenceCapsule]),
              'value',
              false,
            ) as boolean),
        isUncertain: capsule.isUncertain,
        cursorKey: cursorKeyMilliseconds,
        startTime: xStart,
        endTime: xEnd,
        notFullyVisible,
        yValue,
      });
    }, [])
    .value();

  if (_.isEmpty(capsules)) {
    const emptyRow = {
      id: `${totalYValue}-${condition.id}`,
      uniqueId: `${totalYValue}-${condition.id}`,
      conditionId: condition.id,
      color: condition.color,
      lane: condition.lane,
      selected: false,
      itemType: ITEM_TYPES.CAPSULE,
      isAggregated: false,
      anyCapsulesSelected: false,
      lineWidth: DEFAULT_CAPSULE_LINE_WIDTH,
      data: [],
      capsules: [],
      zones: [],
      yValue: totalYValue,
    } as unknown as CapsuleChartItem;
    chartItems.push(emptyRow);
    totalYValue += 1;
  } else {
    if (!_.isEmpty(chartItems)) {
      totalYValue += (_.chain(prevEnds).keys().map(Number).max().value() ?? 0) + 1;
    }
  }

  if (isEditing) {
    totalYValue += 1;
  }

  // This is how we get Highcharts to display a start and end line that helps us generate an "outline" for an
  // uncertain capsule. If a condition contains uncertain capsules we overlay the uncertain capsule with another
  // uncertain capsule that is just a bit shorter than the original capsule to generate the start/end outline
  // boundary. This code clones the existing capsules, filters out the uncertain capsules, adjusts the start and
  // end times for those capsules to generate the start and end lines, and adds new ids for capsules and
  // condition, as well as the itemType UNCERTAIN_BOUNDED_CAPSULE.
  if (_.some(capsules, 'isUncertain')) {
    const uncertainItems = _.chain(chartItems)
      .map((row) => {
        // the width of the start and end boundary needs to be calculated based on the display range duration to
        // ensure it looks consistent for any given display range. This calculation below has proven to result in
        // the desirable width.
        const firstUncertainCapsule = _.find(row.capsules, 'isUncertain');
        // If the uncertain capsule has a cursorKey, set that as the uncertainty start
        const unboundedCapsuleUncertaintyStart = firstUncertainCapsule ? firstUncertainCapsule.cursorKey : undefined;
        // Make sure that we're only displaying uncertainty if the start of the uncertainty is before the end of the
        // capsule, if there is a cursorKey. If there is not a cursor key, then the capsule is bounded, defaulting to
        // true for the below check.
        const displayUncertainty = unboundedCapsuleUncertaintyStart
          ? unboundedCapsuleUncertaintyStart <= (firstUncertainCapsule?.endTime ?? Infinity)
          : true;
        // We want to display an uncertain box if the capsule exists, and either doesn't have a cursor (bounded) or
        // has a cursor and the cursor is <= end time (unbounded)>
        if (firstUncertainCapsule && displayUncertainty) {
          const newRow = _.cloneDeep(row);
          const lineWidthInTime = (context.displayRangeDurationMs / 1000) * 1.4;
          const uncertainAfter = firstUncertainCapsule.startTime;
          newRow.isUncertain = true;
          newRow.conditionId = `${newRow.conditionId}_uncertain`;
          newRow.id = `${newRow.yValue}-${newRow.conditionId}`;
          newRow.uniqueId = `${newRow.yValue}-${newRow.conditionId}`;
          newRow.color = context.darkMode ? DARK_COLOR_FOR_UNCERTAIN_PORTION : LIGHT_COLOR_FOT_UNCERTAIN_PORTION_WHITE;
          newRow.itemType =
            unboundedCapsuleUncertaintyStart === firstUncertainCapsule.endTime
              ? ITEM_TYPES.UNCERTAIN_UNBOUNDED_CAPSULE
              : ITEM_TYPES.UNCERTAIN_BOUNDED_CAPSULE;
          // Shrink the line width so that it shows inside the original line
          newRow.lineWidth -= newRow.itemType === ITEM_TYPES.UNCERTAIN_BOUNDED_CAPSULE ? 4 : 5;
          // Data labels should only be shown for the original row
          delete (newRow as { dataLabels?: CapsuleChartItem['dataLabels'] }).dataLabels;
          newRow.data = _.chain(newRow.data)
            .chunk(3)
            .map((group) => {
              if (group[0].x >= uncertainAfter) {
                // Draw a line through each (partially) uncertain capsule to hollow them out.
                // Note: group[0].x is the x value of the beginning of the capsule
                //       group[1].x and group[2].x are both the x value of the end of the capsule.

                const capsuleDuration = group[1].x - group[0].x;
                if (
                  capsuleDuration !== 0 &&
                  newRow.itemType === ITEM_TYPES.UNCERTAIN_UNBOUNDED_CAPSULE &&
                  group[1].x === unboundedCapsuleUncertaintyStart
                ) {
                  // Display the uncertain-unbounded capsules by creating an open end for the last
                  // 5% of the capsule.
                  // Note: this branch is only hit if the cursor key is exactly at the end of this
                  // unbounded uncertain capsule.
                  group[0].x = Math.max(group[0].x, unboundedCapsuleUncertaintyStart - capsuleDuration * 0.05);
                } else if (capsuleDuration !== 0) {
                  // Note: this branch is hit if this capsule is uncertain and there is no cursor key
                  // or the cursor key exists and is _not_ exactly at the end of the capsule.
                  // It could be:
                  //  a. A partially certain or fully uncertain unbounded capsule
                  //      In this case, start the hollowing out at the cursor key
                  //      or the beginning of this capsule, whichever is later.
                  //  b. An uncertain bounded capsule
                  //      Hollow out the whole capsule.
                  const adjustment =
                    capsuleDuration < lineWidthInTime * 3 ? (capsuleDuration / 2 / 100) * 15 : lineWidthInTime;

                  // Adjust the end back so we don't draw over the end, but don't go
                  // before the start.
                  const adjustedEnd = Math.max(group[0].x, group[1].x - adjustment);
                  group[1].x = adjustedEnd;
                  group[2].x = adjustedEnd;

                  // Adjust the start, but don't go beyond the adjusted end.
                  const adjustedStart = Math.min(group[0].x + adjustment, adjustedEnd);

                  group[0].x = unboundedCapsuleUncertaintyStart
                    ? // We have a cursor key; if this is a partially certain capsule, start
                      // the hollowing out at the cursor key.
                      // Otherwise this is a fully uncertain, bounded capsule; hollow out the whole thing.
                      Math.min(adjustedEnd, Math.max(adjustedStart, unboundedCapsuleUncertaintyStart))
                    : adjustedStart;
                }

                // Cursor labels should only be on the original rows
                delete group[0].labelText;
                delete group[1].labelText;

                return group;
              }

              return undefined;
            })
            .compact()
            .flatten()
            .value();

          return newRow;
        }

        return undefined;
      })
      .compact()
      .value();

    return _.flatten([uncertainItems, chartItems]);
  }

  return chartItems;
}

function isTableCapsule(
  capsule: CapsuleItem | CalculatedChartCapsule,
  shouldUseTableCapsules: boolean,
): capsule is CapsuleItem {
  return shouldUseTableCapsules;
}

function getCapsuleTimestampMilliseconds(
  capsule: CapsuleItem | CalculatedChartCapsule,
  field: 'start' | 'end',
  shouldUseTableCapsules: boolean,
): number | undefined | null {
  if (isTableCapsule(capsule, shouldUseTableCapsules)) {
    return capsule[`${field}Time`];
  }

  const value = capsule[field];

  return _.isNil(value) ? value : nanosToMillis(value);
}

function getCapsuleProperties(capsule: CapsuleItem | CalculatedChartCapsule, isChainView: boolean) {
  return isTableCapsule(capsule, isChainView)
    ? capsule.properties
    : _.transform(
        capsule.properties,
        (memo, property) => {
          memo[property.name] = property.value;
        },
        {},
      );
}

function getCapsulePropertiesUOM(capsule: CapsuleItem | CalculatedChartCapsule, isChainView: boolean) {
  return isTableCapsule(capsule, isChainView)
    ? capsule.propertiesUOM
    : _.transform(
        capsule.properties,
        (memo, property) => {
          memo[property.name] = property.unitOfMeasure;
        },
        {},
      );
}
